import React, { Suspense } from 'react';
import { InteractionInfo, OverlayComponentProps } from '$/components/Interactions/InteractionInfo';

import { withWebinarPlayerContext } from '$/components/WebinarPlayer/context';
import { AnimatePresence, motion } from 'framer-motion';
import { useScaledRoomContext } from '$/components/WebinarPlayer/Rooms/RoomParts';
import { css } from '@emotion/react';

import { HasJoined, PlayerMode } from '$/components/WebinarPlayer/types';
import { PageMode } from '$/widget/src/interface';
import { AttendeeInfoFields } from './types';
import { useHasChangedShallow, useHasChangedDeep } from '$/hooks/useHasChanged';
import isMobile from '$/utils/isMobile';
import tw from 'twin.macro';
import { Portal } from '$/components/Portal';
import { OverlayInteractionMode } from '../types';
import {
  AttendeeFragment,
  ReactionForInteractionInput,
  RegistrationFormFieldFragment,
  UngatedSessionType,
  useCreateInteractionReactionMutation,
} from '$/GraphQL/schema/schema';
import { RETARGETED_VISITOR_FNAME } from '$/shared/frontend/types';
import { getFormFieldDefaultValue } from '$/utils/getFormFieldDefaultValue';
import { logger } from '$/utils/logger';
import { useEmbeddedPlayerContext } from '$/components/EmbeddedPlayer/useEmbeddedPlayer';
import { useSocketContext } from '$/components/Socket/SocketContext';
import { useAttendeePropertiesContext } from '$/components/Property/PropertyProvider';
import { ApolloError } from '@apollo/client';
import { useVideoStageArea } from '$/hooks/useVideoStageArea';

const InVideoRegistrationForm = React.lazy(() => import('./InVideoRegistrationForm'));

function haveRequiredFieldValues(
  attendee: AttendeeFragment | undefined,
  requireOnlyBuiltInFields: boolean,
  fields: RegistrationFormFieldFragment[]
): boolean {
  logger.debug('PLAY', 'IWR attendee values', attendee);

  if (
    !attendee ||
    !attendee.email ||
    !attendee.firstName ||
    attendee.firstName === RETARGETED_VISITOR_FNAME
  ) {
    return false;
  }

  // 'Only name and email': Not show registration form when existing name and email
  if (requireOnlyBuiltInFields) {
    return true;
  }

  // 'Full registration form': Not shown registration form when having needed information
  const defaultInfo: AttendeeInfoFields | undefined = attendee?.email
    ? {
        firstName: attendee.firstName ?? undefined,
        lastName: attendee?.lastName ?? undefined,
        email: attendee?.email,
        attendeeFields: attendee?.attendeeFields,
      }
    : undefined;

  // Find any required fields empty value via propertyName
  const hasFieldEmptyValue = fields.find((field) => {
    if (
      field.isRequired &&
      field.propertyName &&
      !['firstName', 'lastName', 'email'].includes(field.propertyName)
    ) {
      if (defaultInfo && defaultInfo?.attendeeFields) {
        const attendeeField = defaultInfo.attendeeFields.find(
          (attendeeField) =>
            field.propertyName?.toLowerCase() === attendeeField.propertyName?.toLowerCase()
        );
        if (attendeeField) return false;
      }
      const defaultValue = getFormFieldDefaultValue(field.propertyName);
      if (defaultValue) return false;

      return true;
    }

    return false;
  });

  return !hasFieldEmptyValue;
}

const InVideoRegistration: React.FC<{
  interaction: InteractionInfo;
  onClose: () => void;
}> = withWebinarPlayerContext(
  [
    'setIsPlaying',
    'attendee',
    'set',
    'ewebinar',
    'dimensions',
    'animations',
    'mode',
    'hasJoined',
    'isCollapsedEmbeddedPlayer',
    'rooms',
    'playbackPositionRef',
    'shortIdRef',
    'currentRoom',
  ],
  'InVideoRegistration'
)(({ interaction, onClose, context }) => {
  const modalRef = React.useRef<HTMLDivElement>(null);
  const { playerInfo } = useEmbeddedPlayerContext();
  const { scale } = useScaledRoomContext();
  const [style, animateStyle] = useVideoStageArea({
    animations: context.animations,
    currentRoom: context.currentRoom,
    dimensions: context.dimensions,
  });
  const roomScale = animateStyle?.scale ?? style.scale;
  const [createReaction] = useCreateInteractionReactionMutation();
  const { connectionId } = useSocketContext();
  const { updateProperties } = useAttendeePropertiesContext();

  const {
    setIsPlaying,
    attendee,
    ewebinar,
    set,
    animations,
    mode,
    dimensions,
    hasJoined,
    isCollapsedEmbeddedPlayer,
    rooms,
    playbackPositionRef,
    shortIdRef,
  } = context;

  React.useEffect(() => {
    if (!isCollapsedEmbeddedPlayer) return;
    modalRef.current?.getElementsByClassName('Modal').item(0)?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [isCollapsedEmbeddedPlayer]);

  const onSkipForm = React.useCallback(() => {
    // Restart video
    animations.start('modal.close');
    onClose();
    setIsPlaying(true, { atPosition: interaction.appearAt(), blockPlayAndSeek: 'none' });
  }, [animations, interaction, onClose, setIsPlaying]);

  const onSubmitReaction = async (): Promise<void> => {
    if (mode === PlayerMode.edit || !attendee) {
      return;
    }

    const detailsKey = interaction.detailsKey();

    const reaction: ReactionForInteractionInput = {
      interactionId: interaction.id,
      ewebinarId: ewebinar.id,
      interactionType: interaction.type,
      reactionAppearRoom: rooms.getRoomAt(playbackPositionRef.current ?? 0).type,
      reactedAt: playbackPositionRef.current ?? 0,
      attendeeId: `${attendee?.id}`,
      detailsFields: detailsKey
        ? {
            [detailsKey]: {
              attendeeId: attendee.id,
              setId: attendee.set.id,
              ungatedSessionType:
                mode === PlayerMode.replay
                  ? UngatedSessionType.Replay
                  : UngatedSessionType.OnDemand,
            },
          }
        : {},
    };

    try {
      const res = await createReaction({
        variables: {
          attendeeId: attendee!.id,
          data: reaction,
          connectionId: connectionId ?? shortIdRef,
        },
      });
      if (res.data?.createInteractionReaction?.reaction?.properties) {
        updateProperties(res.data?.createInteractionReaction.reaction.properties);
      }

      // Close the IVR form
      animations.start('modal.close');
      setIsPlaying(true, { atPosition: interaction.appearAt(), blockPlayAndSeek: 'none' });
    } catch (e) {
      const error = e as ApolloError;
      console.error('Error creating reaction ', { error });
    }
  };

  // Detect the name and email of the ungated attendee from the chat input
  const details = interaction.fragment.details;

  const hideConsentCheckbox = Boolean(
    details?.inVideoRegistration?.hideConsentCheckbox ||
      !ewebinar.registrationFormSettings.showConsentCheckbox ||
      !ewebinar.registrationFormSettings.consentCheckboxText
  );

  return (
    <motion.div
      ref={modalRef}
      initial='modal.close'
      animate={hasJoined === HasJoined.Joined ? 'modal.show' : undefined}
      transition={{ type: 'spring', duration: 0.25 }}
      variants={{
        'modal.show': {
          y: '-50%',
          x: '-50%',
          opacity: 1,
          scale: mode === PlayerMode.edit ? scale : 1,
        },
        'modal.close': {
          y: '-100%',
          x: '-50%',
          opacity: 0,
          scale: mode === PlayerMode.edit ? scale : 1,
        },
      }}
      exit='modal.close'
      className={mode === PlayerMode.edit ? 'scale-overlay' : ''}
      css={css`
        top: 50%;
        left: 50%;
        transform-origin: center top;
        ${tw`relative pointer-events-auto overflow-hidden w-full h-full block!`}
        .RegistrationFormModal {
          ${tw`w-full! h-full!`}
          &.showblur {
            background: rgba(0, 0, 0, 0.6);
            backdrop-filter: blur(8px);
            border-radius: ${isCollapsedEmbeddedPlayer
              ? 20 / roomScale
              : dimensions.videoStage.radius}px;
            overflow: hidden;
          }
          .Modal {
            ${tw`block! overflow-y-auto! shadow-none! w-full! px-0 bg-transparent!`}

            max-height: calc(${dimensions.videoStage.height}px - 6rem);
            max-width: calc(100% - 1rem) !important;

            ::-webkit-scrollbar-thumb {
              background: rgba(128, 135, 139, 0.8);
            }
            .modal-header {
              ${tw`bg-transparent! justify-center!`}
              .EditableInput {
                ${tw`w-full text-white! text-4xl! md:text-6xl! leading-normal! mb-1e! md:mb-2e!`}
                .public-DraftStyleDefault-ltr {
                  text-align: center;
                }
              }
              p {
                ${tw`mb-0!`}
              }
              .form-title {
                ${tw`md:max-w-70e!`}
              }
            }
            .modal-body {
              ${tw`w-full md:w-35e md:max-w-35e mx-auto h-auto!`}
            }
            .modal-footer {
              background: none !important;
              margin-top: initial !important;
              ${tw`relative! px-1e! py-0! overflow-hidden border-0 w-full md:w-35e md:max-w-35e mx-auto`}
              > div {
                ${tw`mt-0 items-center md:text-3xl`}
              }
            }
            .reg-field,
            input,
            textarea .dropdown .menu > .item,
            .reg-field .ui.label.error-message {
              ${tw`text-2xl! leading-tight`}
            }
            .reg-field .ui.label.error-message {
              ${tw`leading-none	`}
            }
            ${mode === PlayerMode.edit &&
            `.reg-field input, button.submit-button {
                  ${tw`pointer-events-none!`}
                }
              `}
            .reg-field .EditableInput {
              ${tw`text-white`}
            }
            button {
              ${tw`text-2xl! md:text-3xl! md:min-h-3_5e!`}
              &.submit-button {
                ${tw`px-2e!`}
              }
            }
            input {
              ${tw`text-color-input-field!`}
              &::placeholder {
                ${tw`opacity-100! text-color-input-placeholder!`}
              }
            }
            .checkbox label {
              ${tw`text-white! pl-3e!`}
              &:before,
              &:after {
                ${tw`text-2xl`}
                top: 5px;
                width: 25px;
                height: 25px;
              }
              &:after {
                top: 8px;
              }
              p {
                ${tw`text-2xl md:text-3xl!`}
              }
            }
          }
        }
        @media screen and (max-width: 767px) {
          transform: none !important;
          ${tw`absolute! top-0! bottom-0! left-0! right-0! h-full! max-h-full! z-over-modal!`}
          max-width: ${isCollapsedEmbeddedPlayer ? `${playerInfo.width}px` : '100%'};
          .modal-root {
            max-width: ${isCollapsedEmbeddedPlayer ? `${playerInfo.width}px` : '100%'};
          }
          .RegistrationFormModal {
            ${tw`w-full! h-full! left-0! top-0! py-2e! px-1e!`}
            .Modal {
              .modal-footer {
                ${tw`w-full! pr-1_5e!`}
                .footer-wrapper {
                  ${tw`flex-wrap! flex-row! justify-end!`}
                  row-gap: 2rem;
                  .button-close {
                    ${tw`mr-auto!`}
                  }
                  .submit-button {
                    ${tw`w-auto!`}
                  }
                  & > div {
                    ${tw`w-auto!`}
                  }
                }
              }
              .checkbox label {
                &:before {
                  top: 3px;
                }
                &:after {
                  top: 6px;
                }
              }
            }
          }
        }
      `}
    >
      <Suspense>
        <InVideoRegistrationForm
          mode={mode === PlayerMode.edit ? PageMode.preview : PageMode.public}
          ewebinar={ewebinar}
          set={set}
          attendee={attendee}
          showOnlyBuiltInFields={details?.inVideoRegistration?.showOnlyBuiltInFields}
          hideConsentCheckbox={hideConsentCheckbox}
          title={details?.title}
          buttonSettings={{
            buttonColor: details?.inVideoRegistration?.buttonColor || undefined,
            buttonText: details?.inVideoRegistration?.buttonText || 'Continue',
          }}
          skipRegistration={!details?.inVideoRegistration?.isRequired}
          onSubmitReaction={onSubmitReaction}
          onClose={onSkipForm}
        />
      </Suspense>
    </motion.div>
  );
});

const InVideoRegistrationOverlay: React.FC<OverlayComponentProps> = withWebinarPlayerContext(
  [
    'mode',
    'ewebinar',
    'hasJoined',
    'attendee',
    'interactionInEdit',
    'playbackPosition',
    'setInteractionState',
    'setActionsPaneState',
    'actionsPaneState',
    'setIsPlaying',
    'isPlaying',
  ],
  'InVideoRegistrationOverlay'
)(({ interaction, overlayMode, context }) => {
  const {
    mode,
    ewebinar,
    hasJoined,
    attendee,
    interactionInEdit,
    playbackPosition,
    setInteractionState,
    setActionsPaneState,
    actionsPaneState,
    setIsPlaying,
    isPlaying,
  } = context;

  const [hasSkipped, setHasSkipped] = React.useState(false);
  const [prevActionsPaneState] = React.useState(actionsPaneState);

  const removeInteractionPermanently = React.useCallback(() => {
    logger.debug('PLAY', 'IWR Removing myself permanently');

    // We have what we need - make this interaction invalid to reduce rendering overhead
    // as this will remove us from the valid interactions list
    setInteractionState((prev) => {
      return {
        ...prev,
        [interaction.shortId]: {
          set: null,
          invalid: true,
        },
      };
    });
  }, [interaction.shortId, setInteractionState]);

  const { changeVersion: attendeeVersion } = useHasChangedDeep([attendee]);
  const showRegistration = React.useMemo(() => {
    attendeeVersion;

    if (!interaction.details?.inVideoRegistration) return false;
    const settings = interaction.details.inVideoRegistration;

    if (mode === PlayerMode.edit) {
      if (interactionInEdit?.id === interaction.id) {
        // When editing this interaction show it at all times >= appearAt
        return Math.floor(playbackPosition) >= Math.floor(interaction.appearAt());
      }
      // Always show in edit if we're on the right second
      return Math.floor(playbackPosition) === Math.floor(interaction.appearAt());
    }

    if (!attendee) {
      return true;
    }

    if (
      hasSkipped ||
      haveRequiredFieldValues(
        attendee,
        settings.showOnlyBuiltInFields,
        ewebinar.registrationFormSettings.fields
      )
    ) {
      // Completely done
      removeInteractionPermanently();
      return false;
    }

    if (
      attendee.email ||
      (!settings.displayIn.includes(UngatedSessionType.Replay) && mode === PlayerMode.replay) ||
      (!settings.displayIn.includes(UngatedSessionType.OnDemand) && mode === PlayerMode.live)
    ) {
      removeInteractionPermanently();
      return false;
    }

    return true;
  }, [
    attendee,
    ewebinar.registrationFormSettings.fields,
    hasSkipped,
    interaction,
    interactionInEdit?.id,
    mode,
    playbackPosition,
    removeInteractionPermanently,
    attendeeVersion,
  ]);

  logger.debug('PLAY', 'IWR showRegistration', showRegistration);

  const hasShowChanged = useHasChangedShallow([showRegistration]);
  React.useEffect(() => {
    if (!hasShowChanged) {
      return;
    }
    logger.debug('PLAY', 'IWR hasShowChanged');

    if (showRegistration) {
      logger.debug('PLAY', 'IWR PAUSE');

      // Force pause of video for registration form at appearAt
      // in case we were already trying to seek further ahead
      // DD: Need to enforce no seeks/playbacks while registration is shown
      // at WebinarPlayer level
      setIsPlaying(
        false,
        mode === PlayerMode.edit
          ? undefined
          : {
              atPosition: interaction.appearAt(),
              blockPlayAndSeek: 'forward',
            }
      );

      if (isPlaying) {
        // Hide actions pane to make input fields as large a possible
        if (actionsPaneState === 'shown' && mode !== PlayerMode.edit) {
          setActionsPaneState('hidden');
        }
      }
    } else {
      if (!isPlaying && mode !== PlayerMode.edit) {
        setIsPlaying(true, { atPosition: interaction.appearAt(), blockPlayAndSeek: 'none' });
        setActionsPaneState(prevActionsPaneState);
      }
    }
  }, [
    actionsPaneState,
    hasShowChanged,
    interaction,
    isPlaying,
    mode,
    prevActionsPaneState,
    setActionsPaneState,
    setIsPlaying,
    showRegistration,
  ]);

  const onClose = React.useCallback(() => {
    setHasSkipped(true);
  }, [setHasSkipped]);

  if (hasJoined !== HasJoined.Joined || !showRegistration) {
    // Skip completely if not joined
    // DD: This should also happen if we aren't embedded - we don't need all the logic underneath
    return null;
  }

  if (overlayMode === OverlayInteractionMode.Edit) {
    return (
      <AnimatePresence>
        <InVideoRegistration interaction={interaction} onClose={onClose} />
      </AnimatePresence>
    );
  }

  if (isMobile && mode !== PlayerMode.edit) {
    return (
      <Portal containerId='VideoControlsArea'>
        <AnimatePresence>
          <InVideoRegistration interaction={interaction} onClose={onClose} />
        </AnimatePresence>
      </Portal>
    );
  }

  // Render form in a portal to fix issue of nested Form
  return (
    <Portal containerId='formOverVideoContainer'>
      <AnimatePresence>
        <InVideoRegistration interaction={interaction} onClose={onClose} />
      </AnimatePresence>
    </Portal>
  );
});

export default InVideoRegistrationOverlay;
